import transV2Service from '@/services/commons/transV2Service';

/**
 * Clinic Dashboard API Service
 * 
 * All endpoints use transV2Service which provides the base URL:
 * - Production: https://api.vibrant-america.com/v2/portal/trans-service
 * - Staging: https://api.vibrant-america.com/v2/portal/trans-service-st
 */

// ============================================================================
// Metrics & Dashboard Data
// ============================================================================

/**
 * Get clinic charting dashboard metrics
 * @param {Object} params - Query parameters
 * @param {string} params.clinic_id - Clinic ID (required)
 * @param {string} [params.provider_id] - Provider ID (optional)
 * @param {string} [params.date_range_preset] - Date range preset: today | last_7_days | last_30_days | last_90_days | last_year | custom
 * @param {string} [params.date_range_start] - Start date (required if preset=custom)
 * @param {string} [params.date_range_end] - End date (required if preset=custom)
 * @param {string[]} [params.metrics] - Array of specific metric IDs to fetch
 * @returns {Promise} Response with metrics array
 */
export async function getMetricsApi(params) {
  try {
    const response = await transV2Service.get('/api/v1/clinic-charting/metrics', { params });
    return response.data;
  } catch (error) {
    console.error('Error getting metrics:', error);
    throw new Error(error?.message ?? 'Failed to get metrics');
  }
}

/**
 * Retrieve raw conversation engagement metrics
 * @param {Object} data - Request body
 * @param {string} data.clinic_id - Clinic ID (required)
 * @param {string} [data.date_range_preset] - Date range preset
 * @param {string} [data.date_range_start] - Start date (required if preset=custom)
 * @param {string} [data.date_range_end] - End date (required if preset=custom)
 * @returns {Promise} Response with engagement metrics
 */
export async function retrieveConversationsApi(data) {
  try {
    const response = await transV2Service.post('/api/v1/clinic-charting/metrics/retrieve-conversations', data);
    return response.data;
  } catch (error) {
    console.error('Error retrieving conversations:', error);
    throw new Error(error?.message ?? 'Failed to retrieve conversations');
  }
}

/**
 * Get order and revenue movement buckets for charting
 * @param {Object} params - Query parameters
 * @param {string} params.clinic_id - Clinic ID (required)
 * @param {string} [params.provider_id] - Provider ID (optional)
 * @param {string} [params.date_range_preset] - Date range preset
 * @param {string} [params.date_range_start] - Start date
 * @param {string} [params.date_range_end] - End date
 * @param {string} [params.period] - Period: daily | weekly | monthly
 * @returns {Promise} Response with revenue movement buckets
 */
export async function getOrderRevenueMovementApi(params) {
  try {
    const response = await transV2Service.get('/api/v1/clinic-charting/order-revenue-movement', { params });
    return response.data;
  } catch (error) {
    console.error('Error getting order revenue movement:', error);
    throw new Error(error?.message ?? 'Failed to get order revenue movement');
  }
}

// ============================================================================
// Preferences & Settings
// ============================================================================

/**
 * Get dashboard metric preferences
 * @param {Object} params - Query parameters
 * @param {string} [params.provider_id] - Provider ID (optional)
 * @returns {Promise} Response with preferences
 */
export async function getPreferencesApi(params) {
  try {
    const response = await transV2Service.get('/api/v1/clinic-charting/preferences', { params });
    return response.data;
  } catch (error) {
    console.error('Error getting preferences:', error);
    throw new Error(error?.message ?? 'Failed to get preferences');
  }
}

/**
 * Update dashboard metric preferences
 * @param {Object} data - Request body
 * @param {string[]} data.pinned_metric_ids - Pinned metric IDs (max 3)
 * @param {string[]} data.key_metric_ids - Key metric IDs (min 3, max 6)
 * @param {string} data.dashboard_scope - Dashboard scope: practice_view | provider_view
 * @param {Object} params - Query parameters
 * @param {string} [params.provider_id] - Provider ID (optional)
 * @returns {Promise} Response with updated preferences
 */
export async function updatePreferencesApi(data, params) {
  try {
    const response = await transV2Service.put('/api/v1/clinic-charting/preferences', data, { params });
    return response.data;
  } catch (error) {
    console.error('Error updating preferences:', error);
    throw new Error(error?.message ?? 'Failed to update preferences');
  }
}

// ============================================================================
// Risk Alerts
// ============================================================================

/**
 * Get at-risk patient alerts
 * @param {Object} params - Query parameters
 * @param {string} params.clinic_id - Clinic ID (required)
 * @param {string} [params.provider_id] - Provider ID (optional)
 * @param {string} [params.status] - Status: active | archived
 * @param {string} [params.cursor] - Pagination cursor
 * @param {number} [params.limit] - Max results (default 50, max 200)
 * @returns {Promise} Response with risk alerts
 */
export async function getRiskAlertsApi(params) {
  try {
    const response = await transV2Service.get('/api/v1/clinic-charting/risk-alerts', { params });
    return response.data;
  } catch (error) {
    console.error('Error getting risk alerts:', error);
    throw new Error(error?.message ?? 'Failed to get risk alerts');
  }
}

/**
 * Resolve a risk alert
 * @param {string} alertId - Alert ID
 * @param {Object} data - Request body
 * @param {string} data.clinic_id - Clinic ID (required)
 * @param {string} data.provider_id - Provider ID (required)
 * @returns {Promise} Response with resolved alert
 */
export async function resolveRiskAlertApi(alertId, data) {
  try {
    const response = await transV2Service.post(`/api/v1/clinic-charting/risk-alerts/${alertId}/resolve`, data);
    return response.data;
  } catch (error) {
    console.error('Error resolving risk alert:', error);
    throw new Error(error?.message ?? 'Failed to resolve risk alert');
  }
}

/**
 * Get room-level AI sentiment analysis
 * @param {Object} params - Query parameters
 * @param {string} params.clinic_id - Clinic ID (required)
 * @param {string} params.room_id - Room ID (required)
 * @returns {Promise} Response with room analysis
 */
export async function getRoomAnalysisApi(params) {
  try {
    const response = await transV2Service.get('/api/v1/clinic-charting/room-analysis', { params });
    return response.data;
  } catch (error) {
    console.error('Error getting room analysis:', error);
    throw new Error(error?.message ?? 'Failed to get room analysis');
  }
}

// ============================================================================
// AI Insights
// ============================================================================

/**
 * Get cached AI insights
 * @param {Object} params - Query parameters
 * @param {string} params.clinic_id - Clinic ID (required)
 * @param {string} [params.provider_id] - Provider ID (optional)
 * @param {string} [params.date_range_preset] - Date range preset
 * @param {string} [params.date_range_start] - Start date
 * @param {string} [params.date_range_end] - End date
 * @param {string} [params.insight_type] - Insight type: executive_summary | sentiment_strategy
 * @returns {Promise} Response with AI insights
 */
export async function getAIInsightsApi(params) {
  try {
    const response = await transV2Service.get('/api/v1/clinic-charting/ai-insights', { params });
    return response.data;
  } catch (error) {
    console.error('Error getting AI insights:', error);
    throw new Error(error?.message ?? 'Failed to get AI insights');
  }
}

/**
 * Submit AI insight feedback
 * @param {Object} data - Request body
 * @param {string} data.clinic_id - Clinic ID (required)
 * @param {string} data.insight_type - Insight type (required)
 * @param {string} data.feedback - Feedback: up | down (required)
 * @param {string} [data.provider_id] - Provider ID (optional)
 * @param {string} [data.generated_at] - Generation timestamp (optional)
 * @param {string} [data.notes] - Feedback notes (optional, max 2000 chars)
 * @returns {Promise} Response with feedback confirmation
 */
export async function submitAIFeedbackApi(data) {
  try {
    const response = await transV2Service.post('/api/v1/clinic-charting/ai-insights/feedback', data);
    return response.data;
  } catch (error) {
    console.error('Error submitting AI feedback:', error);
    throw new Error(error?.message ?? 'Failed to submit AI feedback');
  }
}

// ============================================================================
// Transactions
// ============================================================================

/**
 * Get paginated clinic charting transactions
 * @param {Object} params - Query parameters
 * @param {string} params.clinic_id - Clinic ID (required)
 * @param {string} [params.provider_id] - Provider ID (optional)
 * @param {number} [params.page_size] - Page size (default 50, max 100)
 * @param {string} [params.next_cursor] - Pagination cursor (optional)
 * @param {string} [params.transaction_type] - Transaction type filter (optional)
 * @param {string} [params.payment_method] - Payment method filter (optional)
 * @param {string} [params.payer] - Payer filter (optional)
 * @param {string} [params.search_query] - Search query (optional)
 * @param {string} [params.sort_order] - Sort order: asc | desc (optional)
 * @returns {Promise} Response with transactions
 */
export async function getTransactionsApi(params) {
  try {
    const response = await transV2Service.get('/api/v1/clinic-charting/transactions', { params });
    return response.data;
  } catch (error) {
    console.error('Error getting transactions:', error);
    throw new Error(error?.message ?? 'Failed to get transactions');
  }
}

/**
 * Export transactions as CSV
 * @param {Object} data - Request body (same filters as getTransactionsApi)
 * @returns {Promise} Response with CSV content or export ID
 */
export async function exportTransactionsApi(data) {
  try {
    const response = await transV2Service.post('/api/v1/clinic-charting/transactions/export', data);
    return response.data;
  } catch (error) {
    console.error('Error exporting transactions:', error);
    throw new Error(error?.message ?? 'Failed to export transactions');
  }
}

/**
 * Download async CSV export
 * @param {string} exportId - Export ID
 * @returns {Promise} Response with CSV file
 */
export async function downloadTransactionExportApi(exportId) {
  try {
    const response = await transV2Service.get(`/api/v1/clinic-charting/transactions/export/${exportId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error downloading transaction export:', error);
    throw new Error(error?.message ?? 'Failed to download transaction export');
  }
}

// ============================================================================
// Practice Documents
// ============================================================================

/**
 * List clinic documents
 * @param {Object} params - Query parameters
 * @param {string} params.clinic_id - Clinic ID (required)
 * @param {string} [params.patient_id] - Patient ID (optional)
 * @param {string} [params.source] - Source filter (optional)
 * @param {string} [params.type] - Type filter (optional)
 * @param {string} [params.search] - Search query (optional)
 * @param {number} [params.offset] - Offset (default 0)
 * @param {number} [params.limit] - Limit (default 20, max 100)
 * @returns {Promise} Response with documents list
 */
export async function getDocumentsApi(params) {
  try {
    const response = await transV2Service.get('/api/v1/clinic-charting/documents', { params });
    return response.data;
  } catch (error) {
    console.error('Error getting documents:', error);
    throw new Error(error?.message ?? 'Failed to get documents');
  }
}

/**
 * Get a single document by ID
 * @param {string} documentId - Document ID
 * @returns {Promise} Response with document details
 */
export async function getDocumentByIdApi(documentId) {
  try {
    const response = await transV2Service.get(`/api/v1/clinic-charting/documents/${documentId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting document by ID:', error);
    throw new Error(error?.message ?? 'Failed to get document');
  }
}

/**
 * Upload a new practice document
 * @param {FormData} formData - Form data with file and metadata
 * @returns {Promise} Response with created document
 */
export async function uploadDocumentApi(formData) {
  try {
    const response = await transV2Service.post('/api/v1/clinic-charting/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading document:', error);
    throw new Error(error?.message ?? 'Failed to upload document');
  }
}

/**
 * Update document metadata
 * @param {string} documentId - Document ID
 * @param {Object} data - Metadata to update
 * @returns {Promise} Response with updated document
 */
export async function updateDocumentApi(documentId, data) {
  try {
    const response = await transV2Service.put(`/api/v1/clinic-charting/documents/${documentId}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating document:', error);
    throw new Error(error?.message ?? 'Failed to update document');
  }
}

/**
 * Delete a practice document
 * @param {string} documentId - Document ID
 * @returns {Promise} Response (204 No Content)
 */
export async function deleteDocumentApi(documentId) {
  try {
    const response = await transV2Service.delete(`/api/v1/clinic-charting/documents/${documentId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting document:', error);
    throw new Error(error?.message ?? 'Failed to delete document');
  }
}

/**
 * Batch delete practice documents
 * @param {Object} data - Request body
 * @param {string} data.clinic_id - Clinic ID (required)
 * @param {string[]} data.ids - Document IDs to delete (required)
 * @returns {Promise} Response with deletion results
 */
export async function batchDeleteDocumentsApi(data) {
  try {
    const response = await transV2Service.post('/api/v1/clinic-charting/documents/batch-delete', data);
    return response.data;
  } catch (error) {
    console.error('Error batch deleting documents:', error);
    throw new Error(error?.message ?? 'Failed to batch delete documents');
  }
}

// ============================================================================
// Patient Files
// ============================================================================

/**
 * Upload a patient file
 * @param {FormData} formData - Form data with file and metadata
 * @returns {Promise} Response with uploaded file metadata
 */
export async function uploadPatientFileApi(formData) {
  try {
    const response = await transV2Service.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading patient file:', error);
    throw new Error(error?.message ?? 'Failed to upload patient file');
  }
}

/**
 * List patient files
 * @param {string} clinicId - Clinic ID
 * @param {string} patientId - Patient ID
 * @param {Object} params - Query parameters
 * @param {string} [params.source] - Source filter (optional)
 * @returns {Promise} Response with patient files
 */
export async function getPatientFilesApi(clinicId, patientId, params) {
  try {
    const response = await transV2Service.get(`/files/clinic/${clinicId}/patient/${patientId}`, { params });
    return response.data;
  } catch (error) {
    console.error('Error getting patient files:', error);
    throw new Error(error?.message ?? 'Failed to get patient files');
  }
}

/**
 * Get file metadata by ID
 * @param {string} fileId - File ID
 * @returns {Promise} Response with file metadata
 */
export async function getFileByIdApi(fileId) {
  try {
    const response = await transV2Service.get(`/files/${fileId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting file by ID:', error);
    throw new Error(error?.message ?? 'Failed to get file');
  }
}

/**
 * Download file content
 * @param {string} fileId - File ID
 * @returns {Promise} Response with file binary content
 */
export async function downloadFileApi(fileId) {
  try {
    const response = await transV2Service.get(`/files/decrypt/${fileId}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error downloading file:', error);
    throw new Error(error?.message ?? 'Failed to download file');
  }
}

/**
 * Delete a file
 * @param {string} fileId - File ID
 * @returns {Promise} Response with deletion confirmation
 */
export async function deleteFileApi(fileId) {
  try {
    const response = await transV2Service.delete(`/files/${fileId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw new Error(error?.message ?? 'Failed to delete file');
  }
}

/**
 * Delete all files for a patient
 * @param {string} clinicId - Clinic ID
 * @param {string} patientId - Patient ID
 * @returns {Promise} Response with deletion confirmation
 */
export async function deletePatientFilesApi(clinicId, patientId) {
  try {
    const response = await transV2Service.delete(`/files/clinic/${clinicId}/patient/${patientId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting patient files:', error);
    throw new Error(error?.message ?? 'Failed to delete patient files');
  }
}

// ============================================================================
// Images
// ============================================================================

/**
 * Batch upload chat images
 * @param {FormData} formData - Form data with files
 * @returns {Promise} Response with uploaded images metadata
 */
export async function batchUploadChatImagesApi(formData) {
  try {
    const response = await transV2Service.post('/api/v1/patient-chat/images/batch-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Error batch uploading chat images:', error);
    throw new Error(error?.message ?? 'Failed to batch upload chat images');
  }
}

/**
 * Batch get chat images metadata
 * @param {Object} data - Request body
 * @param {string} data.clinic_id - Clinic ID (required)
 * @param {string} data.patient_id - Patient ID (required)
 * @param {string[]} data.ids - Image IDs (required)
 * @returns {Promise} Response with images metadata
 */
export async function batchGetChatImagesApi(data) {
  try {
    const response = await transV2Service.post('/api/v1/patient-chat/images/batch-get', data);
    return response.data;
  } catch (error) {
    console.error('Error batch getting chat images:', error);
    throw new Error(error?.message ?? 'Failed to batch get chat images');
  }
}

/**
 * Get chat image metadata by ID
 * @param {string} imageId - Image ID
 * @returns {Promise} Response with image metadata
 */
export async function getChatImageByIdApi(imageId) {
  try {
    const response = await transV2Service.get(`/api/v1/patient-chat/images/${imageId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting chat image by ID:', error);
    throw new Error(error?.message ?? 'Failed to get chat image');
  }
}

/**
 * Download chat image content
 * @param {string} imageId - Image ID
 * @returns {Promise} Response with image binary content
 */
export async function downloadChatImageApi(imageId) {
  try {
    const response = await transV2Service.get(`/api/v1/patient-chat/images/decrypt/${imageId}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error downloading chat image:', error);
    throw new Error(error?.message ?? 'Failed to download chat image');
  }
}

/**
 * Delete a chat image
 * @param {string} imageId - Image ID
 * @returns {Promise} Response (204 No Content)
 */
export async function deleteChatImageApi(imageId) {
  try {
    const response = await transV2Service.delete(`/api/v1/patient-chat/images/${imageId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting chat image:', error);
    throw new Error(error?.message ?? 'Failed to delete chat image');
  }
}

/**
 * Batch upload clinic images
 * @param {FormData} formData - Form data with files
 * @returns {Promise} Response with uploaded images metadata
 */
export async function batchUploadClinicImagesApi(formData) {
  try {
    const response = await transV2Service.post('/api/v1/clinic-charting/images/batch-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Error batch uploading clinic images:', error);
    throw new Error(error?.message ?? 'Failed to batch upload clinic images');
  }
}

/**
 * Batch get clinic images metadata
 * @param {Object} data - Request body
 * @param {string} data.clinic_id - Clinic ID (required)
 * @param {string[]} data.ids - Image IDs (required)
 * @returns {Promise} Response with images metadata
 */
export async function batchGetClinicImagesApi(data) {
  try {
    const response = await transV2Service.post('/api/v1/clinic-charting/images/batch-get', data);
    return response.data;
  } catch (error) {
    console.error('Error batch getting clinic images:', error);
    throw new Error(error?.message ?? 'Failed to batch get clinic images');
  }
}

/**
 * Get clinic image metadata by ID
 * @param {string} imageId - Image ID
 * @returns {Promise} Response with image metadata
 */
export async function getClinicImageByIdApi(imageId) {
  try {
    const response = await transV2Service.get(`/api/v1/clinic-charting/images/${imageId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting clinic image by ID:', error);
    throw new Error(error?.message ?? 'Failed to get clinic image');
  }
}

/**
 * Download clinic image content
 * @param {string} imageId - Image ID
 * @returns {Promise} Response with image binary content
 */
export async function downloadClinicImageApi(imageId) {
  try {
    const response = await transV2Service.get(`/api/v1/clinic-charting/images/decrypt/${imageId}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error downloading clinic image:', error);
    throw new Error(error?.message ?? 'Failed to download clinic image');
  }
}

/**
 * Delete a clinic image
 * @param {string} imageId - Image ID
 * @returns {Promise} Response (204 No Content)
 */
export async function deleteClinicImageApi(imageId) {
  try {
    const response = await transV2Service.delete(`/api/v1/clinic-charting/images/${imageId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting clinic image:', error);
    throw new Error(error?.message ?? 'Failed to delete clinic image');
  }
}
